import type IInputChangeHandler from '../IInputChangeHandler';
import ControlType from './ControlType';

export type FormControlElement = HTMLInputElement | HTMLSelectElement;

export default abstract class FormControl<T extends FormControlElement> {
  abstract readonly type: ControlType;

  $inputs: T[];

  changeHandlers = [] as IInputChangeHandler[];

  constructor($inputs: T[]) {
    this.$inputs = $inputs;
  }

  addChangeHandler(handler: IInputChangeHandler) {
    this.changeHandlers.push(handler);

    handler.start();
  }

  startListening() {
    this.changeHandlers.forEach((ch) => ch.start());
  }

  stopListening() {
    this.changeHandlers.forEach((ch) => ch.stop());
  }

  get isRequired(): boolean {
    return this.$inputs[0].hasAttribute('required');
  }

  set isRequired(value: boolean) {
    this.$inputs.forEach(($input) => {
      if (value) {
        $input.setAttribute('required', '1');
      } else {
        $input.removeAttribute('required');
      }
    });
  }

  get isReadonly(): boolean {
    return this.$inputs[0].getAttribute('readonly') === 'true';
  }

  set isReadonly(value: boolean) {
    this.$inputs.forEach(($input) => {
      if (value) {
        $input.setAttribute('readonly', 'readonly');
      } else {
        $input.removeAttribute('readonly');
      }
    });
  }

  get isInvalid(): boolean {
    return this.$inputs[0].getAttribute('aria-invalid') === 'true';
  }

  set isInvalid(value: boolean) {
    this.$inputs.forEach(($input) => {
      $input.setAttribute('aria-invalid', value ? 'true' : 'false');
    });
  }

  get name(): string {
    return this.$inputs[0].name;
  }

  get value(): string {
    return this.$inputs[0].value;
  }

  set value(value: string) {
    if (this.$inputs[0].value !== value) {
      this.$inputs[0].value = value;
      this.$inputs[0].dispatchEvent(new Event('change'));
    }
  }

  setValueSilent(value: string) {
    this.$inputs[0].value = value;
  }

  get elementId() {
    return this.$inputs[0].id;
  }

  get label() {
    let $controlLabel:HTMLElement | undefined | null = null;

    if (this.type === ControlType.Radio || this.type === ControlType.Checkbox) {
      $controlLabel = this.$inputs[0].closest('.form-group')?.querySelector('.control-label');
    } else {
      $controlLabel = this.$inputs[0].labels?.item(0);
    }

    if ($controlLabel) {
      const labelText = $controlLabel.innerText;

      const $requiredIndicator = $controlLabel.querySelector<HTMLSpanElement>('.tn-required-field');

      if ($requiredIndicator) {
        const requiredIndicatorText = $requiredIndicator.innerText;

        if (labelText.endsWith(requiredIndicatorText)) {
          return labelText.slice(0, labelText.length - requiredIndicatorText.length).trim();
        }
      }

      return labelText;
    }

    return undefined;
  }
}
